/**
 * Experiment Manager for B2M Tracker
 *
 * Owns the lifecycle of A/B test assignments on the client:
 *   - Persists assignments in localStorage so a returning user keeps
 *     the same group across tabs and browser restarts.
 *   - Re-asks the backend once an entry is older than the TTL (60 s by
 *     default), so a decision taken on the platform — winner picked,
 *     experiment stopped or archived — reaches users within a minute, while
 *     reloads inside that window stay network-free.
 *   - Serves the last known answer when the backend fails or is slower than
 *     experimentTimeout, so an outage keeps users in their group.
 *   - Emits a single ab_test_view event per (session, experiment, identity,
 *     group) so the server can attribute downstream behaviour to the group.
 */
const CACHE_KEY = 'b2m_experiment_assignments';
const SESSION_EXPOSURE_KEY = 'b2m_experiment_exposed_in_session';
// Customers are told platform decisions apply right away — the TTL is the
// upper bound on that delay, so keep it short.
const DEFAULT_TTL_MS = 60 * 1000; // 60 s

// Negative-cache TTL: how long "the service did not assign this user to this
// experiment" (targeting mismatch, completed without winner…) is remembered —
// long enough that a non-matching user does not hit the network on every
// single assign() call. Never trusted longer than an assignment (see
// _isFresh), so a launch or a winner reaches these users within the same
// window as everyone else.
const DEFAULT_NEGATIVE_TTL_MS = 60 * 1000; // 60 s

// How long a call waits for the service before serving the last known
// answer. Only applies when there is one — a first visit waits as before.
const DEFAULT_TIMEOUT_MS = 1500;

// After a failed assign request (network error, 400 from an invalid id in the
// batch…), don't retry the network for this long — an immediate retry almost
// always fails the same way and a per-page-load retry loop hammers the service.
const FAILURE_COOLDOWN_MS = 30 * 1000;

export default class ExperimentManager {
  constructor(tracker) {
    this.tracker = tracker;
  }

  get ttl() {
    return this._msSetting('experimentCacheTtl', DEFAULT_TTL_MS);
  }

  get negativeTtl() {
    return this._msSetting('experimentNegativeCacheTtl', DEFAULT_NEGATIVE_TTL_MS);
  }

  get timeout() {
    return this._msSetting('experimentTimeout', DEFAULT_TIMEOUT_MS);
  }

  // Numeric check instead of `||`: 0 is a meaningful value (0 TTL = always
  // ask the service, 0 timeout = fall back immediately) and must not
  // silently turn into the default.
  _msSetting(key, fallback) {
    const value = this.tracker.config && this.tracker.config[key];
    return typeof value === 'number' && value >= 0 ? value : fallback;
  }

  _readCache() {
    return this.tracker.storage.get(CACHE_KEY, {}) || {};
  }

  _writeCache(cache) {
    return this.tracker.storage.set(CACHE_KEY, cache);
  }

  _isFresh(entry) {
    if (!entry || !entry.assignedAt) return false;
    // A "not assigned" answer is never trusted longer than an assignment.
    const ttl = entry.negative ? Math.min(this.negativeTtl, this.ttl) : this.ttl;
    if (ttl <= 0) return false;
    return (Date.now() - entry.assignedAt) <= ttl;
  }

  /**
   * The identity experiment bucketing keys on.
   *
   * 'user' (default): the customer-supplied login id when identify()/
   * setUserId() has run, otherwise the device identity. A logged-in user
   * keeps their group across devices, browsers and private windows — the
   * backend persists the assignment per user id. The historical
   * cross-subdomain leak that once forced fingerprint bucketing is closed:
   * b2m_external_user_id is host-only and legacy shared cookies self-heal.
   *
   * 'fingerprint': the device identity regardless of login state —
   * assignments never move with an account (pre-1.0.60 behavior).
   */
  _currentIdentity() {
    if (this.tracker.config &&
        this.tracker.config.experimentIdentitySource === 'fingerprint') {
      return this.tracker.userId.getFingerprintId() || this.tracker.userId.getId();
    }
    return this.tracker.userId.getId() || this.tracker.userId.getFingerprintId();
  }

  /**
   * Cache entries are scoped per (experiment, identity) — the key carries the
   * identity the assignment belongs to. One flat slot per experiment made
   * anonymous↔login transitions evict each other's entry on EVERY page load
   * (integrations assert the login id after an async auth bootstrap), so each
   * load re-fired the whole assign burst. Identity-scoped entries let both
   * phases serve from cache, and a foreign identity's entry is structurally
   * invisible — the next user can never inherit groups.
   */
  _entryKey(experimentId, identity) {
    return `${experimentId}::${identity}`;
  }

  /**
   * The entry for this experiment under the given identity. Falls back to a
   * legacy flat entry (pre-scoping SDKs) when it belongs to this identity —
   * entries written without a userId are trusted, as before.
   */
  _entryFor(cache, experimentId, identity) {
    const scoped = cache[this._entryKey(experimentId, identity)];
    if (scoped) return scoped;
    const legacy = cache[experimentId];
    if (legacy && (!legacy.userId || legacy.userId === identity)) return legacy;
    return undefined;
  }

  _findMissing(experimentIds, cache, identity = this._currentIdentity()) {
    return experimentIds.filter(id =>
      !this._isFresh(this._entryFor(cache, id, identity))
    );
  }

  /**
   * Wait (bounded) for the async identity resolution to finish. Resolution is
   * fast — a local hash plus at most one 800ms-capped resolve call — so the
   * cap here is generous. On timeout the caller proceeds with the current id;
   * the belongs-to-user cache guard contains any resulting mismatch.
   */
  async _waitForIdentity(maxWaitMs = 3000) {
    const deadline = Date.now() + maxWaitMs;
    while (this.tracker.userId.idIsTemporary && Date.now() < deadline) {
      await new Promise(resolve => setTimeout(resolve, 25));
    }
  }

  /**
   * Device/locale context automatically attached to every assign request so
   * targeting rules keyed on environment (timezone, device, language…) can
   * match without every integration having to collect and pass these values.
   * Caller-supplied properties always win on key conflicts.
   *
   * `timezone` is the IANA name (e.g. "Asia/Tokyo") — the exact key/format the
   * experiments service compares against. One key per concept: the service
   * normalizes property keys to lowercase before rule matching, so the
   * historical device/DeviceType/device_type triple (added when matching was
   * case-sensitive) collapsed to the single snake_case spelling.
   * NOTE: lowercase normalization alone does not equate `devicetype` with
   * `device_type` — if any rule was hand-authored as `DeviceType`, the service
   * side must also alias/strip separators for it to keep matching.
   * Also guarantees the payload is never an empty object, which the
   * experiments service rejects with 400 "Properties are required".
   *
   * @returns {Object}
   */
  _defaultTargetingProperties() {
    const props = {};
    try {
      const auto = this.tracker.browserInfo.getAutoProperties() || {};
      if (auto.timezone) props.timezone = auto.timezone;
      if (auto.device_type) props.device_type = auto.device_type;
      if (auto.language) props.language = auto.language;
      if (auto.os) props.os = auto.os;
      if (auto.browser) props.browser = auto.browser;
    } catch (e) {
      this.tracker.logger.warn('[B2M Experiments] default targeting properties failed:', e);
    }
    return props;
  }

  /**
   * Resolve group assignments for the requested experiments.
   *
   * @param {string[]} experimentIds
   * @param {Object} [properties={}] - targeting attributes (country, device, …)
   * @param {Object} [options]
   * @param {boolean} [options.forceRefresh=false] - bypass cache and re-fetch all
   * @param {boolean} [options.deferExposure=false] - do NOT emit ab_test_view here;
   *   the caller fires trackExposure() itself once the variant is actually used
   *   (visual runtime: when the DOM changes are applied)
   * @returns {Promise<Array<{ExperimentId, GroupId, GroupName}>>}
   */
  async assign(experimentIds, properties = {}, options = {}) {
    if (!Array.isArray(experimentIds) || experimentIds.length === 0) {
      this.tracker.logger.warn('[B2M Experiments] assign called without experiment IDs');
      return [];
    }

    // Identity can resolve asynchronously (deviceClass mode's first visit,
    // storage-blocked fallback). Wait for the final device id so an anonymous
    // assignment is never keyed on a throwaway temp. When a login id is
    // present and bucketing keys on it (default 'user' source), there is
    // nothing to wait for — the external id is already final.
    if (this.tracker.userId.idIsTemporary &&
        !(this.tracker.config.experimentIdentitySource !== 'fingerprint' &&
          this.tracker.userId.getExternalId())) {
      await this._waitForIdentity();
    }

    // Bucketing key: the login id when identified, the device id otherwise
    // (see _currentIdentity for the identity-source contract). Cache entries
    // are scoped to this identity, so a login/logout mid-page re-resolves
    // against the backend for the NEW identity while the previous identity's
    // entries stay cached for its next phase (no eviction ping-pong).
    const userId = this._currentIdentity();
    if (!userId) {
      this.tracker.logger.warn('[B2M Experiments] assign skipped — user ID not ready');
      return [];
    }

    const { forceRefresh = false, deferExposure = false } = options;
    let cache = this._readCache();
    let missing = forceRefresh ? experimentIds.slice() : this._findMissing(experimentIds, cache, userId);

    // Merge auto-collected context under the caller's explicit properties.
    const targetingProperties = { ...this._defaultTargetingProperties(), ...(properties || {}) };

    // In-flight guard: concurrent assign() calls (React StrictMode double
    // effects, multiple snippets on one page, SPA route + app-boot racing)
    // must not fire duplicate network requests. Wait for the ongoing fetch,
    // then re-check the cache — if it now covers our ids, no request at all.
    // forceRefresh keeps its explicit contract: it waits once (so it can't
    // race the in-flight write) but still refetches its own ids afterwards.
    while (missing.length > 0 && this._assignInFlight) {
      this.tracker.logger.log('[B2M Experiments] assign already in flight — waiting for it');
      try { await this._assignInFlight; } catch (e) { /* owner call logs the failure */ }
      cache = this._readCache();
      if (forceRefresh) break;
      missing = this._findMissing(experimentIds, cache, userId);
    }

    // Failure cooldown: if the last network attempt just failed, serve the
    // cache instead of retrying immediately. forceRefresh bypasses it.
    if (missing.length > 0 && !forceRefresh &&
        this._lastAssignFailureAt && (Date.now() - this._lastAssignFailureAt) < FAILURE_COOLDOWN_MS) {
      this.tracker.logger.log('[B2M Experiments] recent assign failure — cooling down, serving cache');
      missing = [];
    }

    if (missing.length > 0) {
      const request = this._fetchAndStore(userId, missing, targetingProperties);
      this._assignInFlight = request;
      // Only clear our own marker — a later call may have chained a new one.
      request.then(() => { if (this._assignInFlight === request) this._assignInFlight = null; });

      // Put a deadline on the service only when every requested experiment
      // has a last known answer for this identity to fall back on. Without
      // one (first visit) we wait as before — timing out would show control
      // to a user the service is about to put in a variant.
      const canFallBack = missing.every(id => this._entryFor(cache, id, userId));
      if (canFallBack) {
        const answered = await this._settlesWithin(request, this.timeout);
        if (!answered) {
          this.tracker.logger.warn(
            `[B2M Experiments] service slower than ${this.timeout}ms, serving last known assignments`
          );
        }
      } else {
        await request;
      }
      // A successful answer rewrote the cache; a failed or late one left the
      // last known assignments in place (fail-soft).
      cache = this._readCache();
    } else {
      this.tracker.logger.log('[B2M Experiments] All assignments served from cache');
    }

    const result = experimentIds
      .map(id => {
        // Identity-scoped lookup: a foreign identity's entry is invisible by
        // key, so the fail-soft path can never serve a variant the backend
        // did not assign to THIS identity.
        const entry = this._entryFor(cache, id, userId);
        if (!entry || entry.negative) return null;
        return {
          ExperimentId: id,
          GroupId: entry.groupId,
          GroupName: entry.groupName
        };
      })
      .filter(Boolean);

    if (!deferExposure) this._trackExposure(result);
    return result;
  }

  /**
   * Fetch assignments for `missing` and write them to the cache. Never
   * rejects: a failure starts the cooldown and leaves the cache untouched so
   * callers serve the last known assignments. The write lives here (not in
   * the caller) so an answer that arrives after experimentTimeout still
   * lands for the next call.
   */
  _fetchAndStore(userId, missing, targetingProperties) {
    // Start the request synchronously (callers rely on it being on the wire
    // before assign() first yields); a synchronous throw becomes a rejection.
    let call;
    try {
      call = Promise.resolve(this.tracker.experimentApi.assign(userId, missing, targetingProperties));
    } catch (e) {
      call = Promise.reject(e);
    }
    return call
      .then(fresh => {
        // Re-read: a late answer must not clobber entries written meanwhile.
        const cache = this._readCache();
        const now = Date.now();
        this._lastAssignFailureAt = null;

        // Index the response case-insensitively: the service echoes canonical
        // GUID casing which may differ from what the integrator passed, and a
        // cache written only under the response's casing would never satisfy
        // _findMissing for the requested id — every call would refetch.
        const byLowerId = {};
        fresh.forEach(item => {
          if (!item || !item.ExperimentId) return;
          byLowerId[String(item.ExperimentId).toLowerCase()] = item;
        });

        missing.forEach(id => {
          const item = byLowerId[String(id).toLowerCase()];
          if (item) {
            // Positive entry — stored under the REQUESTED id (all lookups use
            // integrator-supplied ids). The live assign endpoint's DTO has no
            // GroupName, so fall back to the group id: getGroup() and
            // ab_test_view then carry a stable, usable identifier instead of
            // undefined (integrations compare against panel group ids).
            const groupName = (item.GroupName != null && item.GroupName !== '')
              ? item.GroupName
              : (item.GroupId != null ? String(item.GroupId) : null);
            cache[this._entryKey(id, userId)] = {
              groupId: item.GroupId,
              groupName,
              assignedAt: now,
              userId
            };
          } else {
            // Negative entry — the service answered but did NOT assign this
            // experiment (targeting mismatch, completed without winner…).
            // Remember that briefly so non-matching users stop hitting the
            // network on every assign() call.
            cache[this._entryKey(id, userId)] = { negative: true, assignedAt: now, userId };
          }
        });

        this._writeCache(cache);
        this.tracker.logger.log('[B2M Experiments] Assignments refreshed:', fresh);
      })
      .catch(e => {
        this._lastAssignFailureAt = Date.now();
        this.tracker.logger.error('[B2M Experiments] assign failed, falling back to cache:', e);
      });
  }

  /** Resolves true if `promise` settles within `ms`, false otherwise. */
  _settlesWithin(promise, ms) {
    return new Promise(resolve => {
      const timer = setTimeout(() => resolve(false), ms);
      promise.then(() => {
        clearTimeout(timer);
        resolve(true);
      });
    });
  }

  /**
   * Emit the exposure for assignments resolved with `deferExposure: true`.
   * Same once-per-(session, experiment, identity, group) dedupe as the assign path;
   * `extraParams` ride on the ab_test_view payload (e.g. experiment_type).
   * @returns {string[]} experiment ids whose exposure was emitted now
   */
  trackExposure(assignments, extraParams = {}) {
    return this._trackExposure(assignments, extraParams);
  }

  /**
   * Synchronous lookup — returns the cached group name for an experiment
   * or null if the user has not been assigned yet.
   */
  getGroup(experimentId) {
    const entry = this._entryFor(this._readCache(), experimentId, this._currentIdentity());
    if (!entry || entry.negative) return null;
    return entry.groupName != null ? entry.groupName : null;
  }

  /**
   * Returns the full cached assignment object including group ID.
   */
  getAssignment(experimentId) {
    const entry = this._entryFor(this._readCache(), experimentId, this._currentIdentity());
    if (!entry || entry.negative) return null;
    return {
      ExperimentId: experimentId,
      GroupId: entry.groupId,
      GroupName: entry.groupName
    };
  }

  /**
   * Wipe cached assignments. Call this when the user logs out or
   * their identity changes so the next user does not inherit groups.
   */
  clear() {
    this.tracker.storage.remove(CACHE_KEY);
    this.tracker.storage._sessionRemove(SESSION_EXPOSURE_KEY);
  }

  /**
   * Emit ab_test_view exactly once per (browser session, experiment,
   * identity, group) so backend attribution stays clean while still
   * capturing every unique exposure — including a user moved to another
   * group mid-session (winner picked, experiment relaunched).
   */
  _trackExposure(assignments, extraParams = {}) {
    const emitted = [];
    if (!assignments || assignments.length === 0) return emitted;

    const exposed = this.tracker.storage._sessionGet(SESSION_EXPOSURE_KEY) || {};
    const identity = this._currentIdentity();
    let updated = false;

    assignments.forEach(({ ExperimentId, GroupId, GroupName }) => {
      // Once per (session, experiment, identity): each identity phase gets its
      // own exposure so backend attribution sees both sides of a login. The
      // marker holds the group id so a group change is a new exposure;
      // `true` (written before groups were tracked) counts as exposed.
      const exposureKey = this._entryKey(ExperimentId, identity);
      const seen = exposed[exposureKey];
      if (seen === true || (seen && seen === GroupId) || exposed[ExperimentId]) return;

      try {
        if (!(window.b2m && typeof window.b2m.track === 'function')) {
          // Tracker not ready — do NOT mark as exposed, so the next assign()
          // in this session retries instead of silently losing the exposure.
          this.tracker.logger.warn(
            '[B2M Experiments] tracker not ready, exposure deferred for:', ExperimentId
          );
          return;
        }
        window.b2m.track('ab_test_view', {
          experiment_id: ExperimentId,
          group_id: GroupId,
          group_name: GroupName,
          ...(extraParams && typeof extraParams === 'object' ? extraParams : {})
        });
        exposed[exposureKey] = GroupId || true;
        updated = true;
        emitted.push(ExperimentId);
      } catch (e) {
        this.tracker.logger.error('[B2M Experiments] exposure tracking failed:', e);
      }
    });

    if (updated) {
      this.tracker.storage._sessionSet(SESSION_EXPOSURE_KEY, exposed);
    }
    return emitted;
  }
}
